import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../context/auth-context.jsx';
import { adminApi } from '../../../lib/admin-api/index.js';
import './payment-settings-page.css';
import { buildSettingsDraft, buildSettingsPayload, emptyFieldValue, mapSettingsErrors, reorderItems } from '../model/payment-settings.model.js';

function SchemaField({ field, value, onChange, path, errors, t }) {
  const id = `payment-setting-${path}`;
  const error = errors[path];
  const inputProps = { id, 'aria-invalid': Boolean(error), 'aria-describedby': error ? `${id}-error` : undefined, maxLength: field.maxLength };
  let control;
  if (field.type === 'boolean') {
    control = <input {...inputProps} type="checkbox" className="form-check-input" checked={Boolean(value)} onChange={(event) => onChange(event.target.checked)} />;
  } else if (field.type === 'select') {
    control = <select {...inputProps} className="form-select" value={value || ''} onChange={(event) => onChange(event.target.value)}>
      <option value="">{t('paymentSettings.selectValue', 'Select a value')}</option>
      {(field.options || []).map((option) => <option key={option} value={option}>{option}</option>)}
    </select>;
  } else if (field.type === 'list') {
    const items = Array.isArray(value) ? value : [];
    control = <div className="payment-settings-tags" role="group" aria-labelledby={`${id}-label`}>
      {items.map((item, index) => <div className="payment-settings-tag" key={index}>
        <input id={`${id}-${index}`} type="text" aria-label={`${field.label} ${index + 1}`} aria-invalid={Boolean(errors[`${path}.${index}`])} aria-describedby={errors[`${path}.${index}`] ? `${id}-${index}-error` : undefined} maxLength={field.maxLength} size={Math.max(4, Math.min(24, String(item).length + 1))} value={item} onChange={(event) => onChange(items.map((row, position) => position === index ? event.target.value : row))} />
        <button type="button" className="payment-settings-icon-button" aria-label={`${t('paymentSettings.remove', 'Remove')} ${field.label} ${index + 1}`} onClick={() => onChange(items.filter((_, position) => position !== index))}>×</button>
        {errors[`${path}.${index}`] && <span id={`${id}-${index}-error`} className="text-danger small" role="alert">{errors[`${path}.${index}`]}</span>}
      </div>)}
      <button type="button" className="btn btn-link btn-sm" disabled={field.maxItems !== undefined && items.length >= field.maxItems} onClick={() => onChange([...items, ''])}>+ {t('paymentSettings.add', 'Add')} {field.label}</button>
    </div>;
  } else if (field.type === 'group') {
    const items = Array.isArray(value) ? value : [];
    control = <div className="payment-settings-records">
      {!items.length && <p className="payment-settings-muted mb-2">{t('paymentSettings.noRecords', 'No entries configured')}</p>}
      {items.map((item, index) => <div className="payment-settings-record" key={index}>
        <div className="payment-settings-record-header">
          <span className="fw-semibold">{field.label} {index + 1}</span>
          <div className="d-flex gap-1">
            <button type="button" className="payment-settings-icon-button" aria-label={`${t('paymentSettings.move', 'Move')} ${field.label} ${t('paymentSettings.item', 'item')} ${index + 1} ${t('paymentSettings.up', 'up')}`} disabled={index === 0} onClick={() => onChange(reorderItems(items, index, index - 1))}>↑</button>
            <button type="button" className="payment-settings-icon-button" aria-label={`${t('paymentSettings.move', 'Move')} ${field.label} ${t('paymentSettings.item', 'item')} ${index + 1} ${t('paymentSettings.down', 'down')}`} disabled={index === items.length - 1} onClick={() => onChange(reorderItems(items, index, index + 1))}>↓</button>
            <button type="button" className="payment-settings-icon-button" aria-label={`${t('paymentSettings.remove', 'Remove')} ${field.label} ${index + 1}`} onClick={() => onChange(items.filter((_, position) => position !== index))}>×</button>
          </div>
        </div>
        {(field.fields || []).map((child) => <SchemaField key={child.key} field={child} value={item[child.key] ?? emptyFieldValue(child)} path={`${path}.${index}.${child.key}`} errors={errors} t={t} onChange={(next) => onChange(items.map((row, position) => position === index ? { ...row, [child.key]: next } : row))} />)}
      </div>)}
      <button type="button" className="btn btn-link btn-sm px-0" disabled={field.maxItems !== undefined && items.length >= field.maxItems} onClick={() => onChange([...items, Object.fromEntries((field.fields || []).map((child) => [child.key, emptyFieldValue(child)]))])}>{t('paymentSettings.add', 'Add')} {field.label}</button>
    </div>;
  } else {
    control = <input {...inputProps} type="text" className="form-control" value={value || ''} onChange={(event) => onChange(event.target.value)} />;
  }
  return <div className={`payment-settings-field ${field.type === 'group' ? 'payment-settings-field-group' : ''}`}>
    {field.type === 'list' || field.type === 'group'
      ? <div id={`${id}-label`} className="payment-settings-label">{field.label}{field.required ? ' *' : ''}</div>
      : <label className="payment-settings-label" htmlFor={id}>{field.label}{field.required ? ' *' : ''}</label>}
    <div className="payment-settings-control">{control}{error && <div id={`${id}-error`} className="text-danger small mt-1" role="alert">{error}</div>}</div>
  </div>;
}

function SecretField({ field, detail, draft, setDraft, error, t }) {
  const configured = Boolean(detail.secrets?.[field.key]);
  const [editing, setEditing] = useState(!configured);
  const clearing = draft.clearSecrets.includes(field.key);
  const id = `payment-secret-${field.key}`;
  function cancelEdit() {
    setDraft((current) => ({ ...current, replacements: { ...current.replacements, [field.key]: '' } }));
    setEditing(false);
  }
  return <div className="payment-settings-field payment-settings-secret">
    <label className="payment-settings-label" htmlFor={editing && !clearing ? id : undefined}>{field.label}{field.required ? ' *' : ''}</label>
    <div className="payment-settings-control">
      {clearing ? <div className="payment-settings-secret-status"><span className="text-danger">{t('paymentSettings.willClear', 'The stored secret will be cleared when you save.')}</span><button type="button" className="btn btn-link btn-sm" onClick={() => setDraft((current) => ({ ...current, clearSecrets: current.clearSecrets.filter((key) => key !== field.key) }))}>{t('paymentSettings.undoClear', 'Undo clear')}</button></div>
      : editing ? <div>
        <div className="d-flex align-items-center gap-2">
          <input id={id} type="password" autoComplete="new-password" className="form-control" maxLength={field.maxLength} value={draft.replacements[field.key] || ''} aria-invalid={Boolean(error)} aria-describedby={`${id}-help${error ? ` ${id}-error` : ''}`} onChange={(event) => setDraft((current) => ({ ...current, replacements: { ...current.replacements, [field.key]: event.target.value } }))} />
          {configured && <button type="button" className="btn btn-link btn-sm" onClick={cancelEdit}>{t('paymentSettings.cancel', 'Cancel')}</button>}
        </div>
        <div id={`${id}-help`} className="form-text">{t('paymentSettings.replaceHelp', 'Enter a new value. The stored secret stays unchanged until you save.')}</div>
      </div> : <div className="payment-settings-secret-status">
        <span className={configured ? 'payment-settings-configured' : 'payment-settings-muted'}><span aria-hidden="true">{configured ? '✓ ' : ''}</span>{configured ? t('paymentSettings.configured', 'Configured') : t('paymentSettings.secretMissing', 'No secret configured')}</span>
        <div className="d-flex align-items-center gap-2">
          <button type="button" className="btn btn-link btn-sm" aria-label={`${t('paymentSettings.change', 'Change')} ${field.label}`} onClick={() => setEditing(true)}>{t('paymentSettings.change', 'Change')}</button>
          {configured && <details className="payment-settings-secret-menu"><summary aria-label={`${t('paymentSettings.moreActions', 'More actions')}: ${field.label}`}>⋮</summary><div><button type="button" className="btn btn-link text-danger" onClick={() => { setEditing(false); setDraft((current) => ({ ...current, replacements: { ...current.replacements, [field.key]: '' }, clearSecrets: [...current.clearSecrets, field.key] })); }}>{t('paymentSettings.clearSecret', 'Clear stored secret')}</button></div></details>}
        </div>
      </div>}
      {error && <div id={`${id}-error`} role="alert" className="text-danger small mt-1">{error}</div>}
    </div>
  </div>;
}

function ProviderForm({ detail, draft, setDraft, errors, disabled, t }) {
  const connectionFields = detail.fields.filter((field) => field.type === 'secret' || field.key === 'environment');
  const paymentFields = detail.fields.filter((field) => !connectionFields.includes(field));
  function renderField(field) {
    return field.type === 'secret'
      ? <SecretField key={field.key} {...{ field, detail, draft, setDraft, t }} error={errors[field.key]} />
      : <SchemaField key={field.key} field={field} value={draft.values[field.key]} path={field.key} errors={errors} t={t} onChange={(value) => setDraft((current) => ({ ...current, values: { ...current.values, [field.key]: value } }))} />;
  }
  return <fieldset disabled={disabled}>
    <div className="payment-settings-provider-header">
      <div><h2>{detail.name}</h2><p className="payment-settings-muted mb-0">{t('paymentSettings.providerConfiguration', 'Provider configuration')}</p></div>
      <div className="form-check form-switch d-flex align-items-center gap-3 ps-0 mb-0">
        <label htmlFor="payment-provider-enabled" className="form-check-label">{t('paymentSettings.enableProvider', 'Enable provider')}</label>
        <input id="payment-provider-enabled" className="form-check-input ms-0 mt-0" type="checkbox" checked={draft.enabled} onChange={(event) => setDraft((current) => ({ ...current, enabled: event.target.checked }))} />
      </div>
    </div>
    {connectionFields.length > 0 && <section className="payment-settings-section" aria-labelledby="payment-connection-heading"><h3 id="payment-connection-heading">{t('paymentSettings.connection', 'Connection')}</h3><p className="payment-settings-muted payment-settings-section-help">{t('paymentSettings.credentialsHelp', 'Credentials are stored securely.')}</p>{connectionFields.map(renderField)}</section>}
    {paymentFields.length > 0 && <section className="payment-settings-section" aria-labelledby="payment-options-heading"><h3 id="payment-options-heading">{t('paymentSettings.paymentOptions', 'Payment options')}</h3>{paymentFields.map(renderField)}</section>}
  </fieldset>;
}

// This module is eagerly imported by app-route before BrowserRouter mounts.
// Native popstate dispatch on Window follows registration order in Chromium.
let activeHistoryGuard = null;
if (typeof window !== 'undefined') {
  window.addEventListener('popstate', (event) => activeHistoryGuard?.(event), true);
}

export default function PaymentSettingsPage() {
  const { token, hasPermission } = useAuth();
  const canManage = hasPermission('settings.manage');
  const { t } = useTranslation();
  const { providerId: routeProviderId } = useParams();
  const navigate = useNavigate();
  const [providers, setProviders] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const providerId = routeProviderId || selectedId;
  const [detail, setDetail] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [listError, setListError] = useState('');
  const [success, setSuccess] = useState('');
  const [copyFeedback, setCopyFeedback] = useState('');
  const [errors, setErrors] = useState({});
  const [conflict, setConflict] = useState(false);
  const [reload, setReload] = useState(0);
  const generation = useRef(0);
  const [draftVersion, setDraftVersion] = useState(0);
  const dirty = Boolean(detail && draft && JSON.stringify(buildSettingsPayload(detail, draft)) !== JSON.stringify(buildSettingsPayload(detail, buildSettingsDraft(detail))));

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event) => { event.preventDefault(); event.returnValue = ''; };
    const entryIndex = window.history.state?.idx;
    const entryPath = window.location.pathname + window.location.search;
    const confirmLeave = () => window.confirm(t('paymentSettings.confirmDiscard', 'Discard unsaved changes?'));
    const guardHistory = (event) => {
      const nextIndex = event.state?.idx;
      if (window.location.pathname + window.location.search === entryPath || typeof entryIndex !== 'number' || typeof nextIndex !== 'number' || nextIndex === entryIndex) return;
      if (!confirmLeave()) {
        // BrowserRouter uses a popstate listener. Restore the entry before it can discard this draft.
        event.stopImmediatePropagation();
        window.history.go(entryIndex - nextIndex);
      }
    };
    const guardLink = (event) => {
      if (event.defaultPrevented || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      const anchor = event.target instanceof Element ? event.target.closest('a[href]') : null;
      if (!anchor || anchor.hasAttribute('download') || (anchor.target && anchor.target !== '_self')) return;
      const target = new URL(anchor.href, window.location.href);
      if (target.origin !== window.location.origin || target.pathname + target.search === entryPath) return;
      if (!confirmLeave()) { event.preventDefault(); event.stopPropagation(); }
    };
    window.addEventListener('beforeunload', warn);
    activeHistoryGuard = guardHistory;
    document.addEventListener('click', guardLink, true);
    return () => {
      window.removeEventListener('beforeunload', warn);
      if (activeHistoryGuard === guardHistory) activeHistoryGuard = null;
      document.removeEventListener('click', guardLink, true);
    };
  }, [dirty, t]);

  function confirmDiscard() {
    return !dirty || window.confirm(t('paymentSettings.confirmDiscard', 'Discard unsaved changes?'));
  }

  function switchProvider(nextId) {
    if (nextId === providerId || saving || !confirmDiscard()) return;
    setDraft(null);
    setDetail(null);
    setSelectedId(nextId);
    navigate(`/payment-settings/providers/${encodeURIComponent(nextId)}`);
  }

  function discard() {
    setDraft(buildSettingsDraft(detail));
    setDraftVersion((version) => version + 1);
    setErrors({});
    setError('');
    setSuccess('');
    setCopyFeedback('');
  }

  useEffect(() => {
    let active = true;
    setLoadingList(true);
    setListError('');
    adminApi.getPaymentSettingsProviders(token).then((response) => {
      if (!active) return;
      setProviders(response.providers || []);
      setSelectedId((current) => current || response.providers?.[0]?.providerId || '');
    }).catch(() => { if (active) setListError(t('paymentSettings.loadFailed', 'Unable to load payment settings.')); })
      .finally(() => { if (active) setLoadingList(false); });
    return () => { active = false; };
  }, [token, reload, t]);

  useEffect(() => {
    const current = ++generation.current;
    setDetail(null);
    setDraft(null);
    setErrors({});
    setError('');
    setSuccess('');
    setCopyFeedback('');
    setConflict(false);
    setSaving(false);
    if (!providerId) return undefined;
    setLoadingDetail(true);
    adminApi.getPaymentSettingsProvider(token, providerId).then((response) => {
      if (generation.current !== current) return;
      setDetail(response);
      setDraft(buildSettingsDraft(response));
    }).catch(() => { if (generation.current === current) setError(t('paymentSettings.loadFailed', 'Unable to load payment settings.')); })
      .finally(() => { if (generation.current === current) setLoadingDetail(false); });
    return () => { generation.current += 1; };
  }, [providerId, token, reload, t]);

  async function save(event) {
    event.preventDefault();
    if (!canManage || saving || conflict || !detail || !draft || !dirty) return;
    const current = generation.current;
    setSaving(true);
    setErrors({});
    setError('');
    setSuccess('');
    setCopyFeedback('');
    try {
      const response = await adminApi.updatePaymentSettingsProvider(token, providerId, buildSettingsPayload(detail, draft));
      if (current !== generation.current) return;
      setDetail(response);
      setDraft(buildSettingsDraft(response));
      setProviders((items) => items.map((item) => item.providerId === providerId ? { ...item, enabled: response.enabled, configured: Boolean(response.configured) } : item));
      setSuccess(t('paymentSettings.saved', 'Payment settings saved.'));
    } catch (requestError) {
      if (current !== generation.current) return;
      const fieldErrors = mapSettingsErrors(requestError);
      const codes = [requestError?.body?.code, ...(requestError?.body?.errors || requestError?.errors || []).map((entry) => entry.code)];
      setErrors(fieldErrors);
      const operationsPending = codes.some((code) => String(code) === '42228');
      setConflict(requestError?.status === 409 && !operationsPending);
      setError(operationsPending
        ? t('paymentSettings.operationsPending', 'Finish or reconcile pending payment operations before changing these settings.')
        : requestError?.status === 409
        ? t('paymentSettings.conflict', 'Settings changed since you opened them. Reload settings before saving again.')
        : codes.some((code) => String(code) === '42218')
          ? t('paymentSettings.domainRequired', 'Publish an HTTPS client domain before enabling this payment provider.')
          : fieldErrors._form || t('paymentSettings.saveFailed', 'Unable to save payment settings. Check the fields and try again.'));
    } finally {
      if (current === generation.current) setSaving(false);
    }
  }

  async function copyCallback() {
    try {
      await navigator.clipboard.writeText(detail.callbackUrl);
      setCopyFeedback(t('paymentSettings.copied', 'Callback URL copied.'));
    } catch {
      setError(t('paymentSettings.copyFailed', 'Unable to copy. Select and copy the callback URL manually.'));
    }
  }

  return <div className="payment-settings-page">
    <header className="payment-settings-page-header"><h1 className="page-header">{t('paymentSettings.title', 'Payment providers')}</h1><p className="payment-settings-muted mb-0">{t('paymentSettings.description', 'Manage payment methods and provider connections.')}</p></header>
    {listError && <div className="alert alert-danger" role="alert">{listError}</div>}
    {error && <div className="alert alert-danger" role="alert">{error}</div>}
    <div className="payment-settings-layout">
      <nav className="payment-settings-catalogue list-group" aria-label={t('paymentSettings.title', 'Payment providers')}>
        {providers.map((provider) => <button type="button" key={provider.providerId} disabled={saving} className={`list-group-item list-group-item-action ${provider.providerId === providerId ? 'active' : ''}`} aria-current={provider.providerId === providerId ? 'page' : undefined} onClick={() => switchProvider(provider.providerId)}>
          <span className="d-block fw-semibold">{provider.name}</span>
          <span className="payment-settings-provider-status"><span aria-hidden="true" className={`payment-settings-status-dot ${provider.enabled ? 'is-enabled' : ''}`} />{provider.enabled ? t('paymentSettings.enabled', 'Enabled') : t('paymentSettings.disabled', 'Disabled')} · {provider.configured ? t('paymentSettings.configured', 'Configured') : t('paymentSettings.notConfigured', 'Not configured')}</span>
        </button>)}
        {!loadingList && !listError && providers.length === 0 && <p>{t('paymentSettings.empty', 'No payment providers are available.')}</p>}
      </nav>
      <div className="payment-settings-content">
        {(loadingList || loadingDetail) && <p role="status">{t('paymentSettings.loading', 'Loading payment settings…')}</p>}
        {detail && draft && <form className="payment-settings-panel" onSubmit={save} noValidate>
          <ProviderForm key={`${providerId}:${detail.revision}:${draftVersion}`} detail={detail} draft={draft} setDraft={setDraft} errors={errors} disabled={!canManage || saving || conflict} t={t} />
          {detail.callbackUrl && <section className="payment-settings-section" aria-labelledby="payment-callback-heading">
            <h3 id="payment-callback-heading">{t('paymentSettings.callback', 'Callback')}</h3><p className="payment-settings-muted payment-settings-section-help">{t('paymentSettings.callbackHelp', 'Add this URL to your provider portal.')}</p>
            <div className="payment-settings-field"><label className="payment-settings-label" htmlFor="payment-callback-url">{t('paymentSettings.callbackUrl', 'Callback URL')}</label>
              <div className="input-group payment-settings-control"><input id="payment-callback-url" type="text" className="form-control payment-settings-url" readOnly value={detail.callbackUrl} /><button type="button" className="btn btn-outline-secondary" aria-label={t('paymentSettings.copyCallback', 'Copy callback URL')} onClick={copyCallback}><i className="bi bi-copy" aria-hidden="true" /> <span>{t('paymentSettings.copy', 'Copy')}</span></button></div>
            </div>
            {copyFeedback && <div className="payment-settings-copy-feedback payment-settings-configured" role="status">{copyFeedback}</div>}
          </section>}
          <footer className="payment-settings-footer">
            <div className="payment-settings-feedback" role="status">{success && !dirty ? <span className="payment-settings-configured">✓ {success}</span> : <span className="payment-settings-muted">{dirty ? t('paymentSettings.unsavedChanges', 'Unsaved changes') : t('paymentSettings.applyAfterSave', 'Changes apply after saving.')}</span>}</div>
            {canManage && <div className="payment-settings-actions">
              {conflict ? <button type="button" className="btn btn-outline-secondary" disabled={saving} onClick={() => { if (confirmDiscard()) setReload((value) => value + 1); }}>{t('paymentSettings.reload', 'Reload settings')}</button> : <button type="button" className="btn btn-link" disabled={saving || !dirty} onClick={discard}>{t('paymentSettings.discardChanges', 'Discard changes')}</button>}
              <button type="submit" className="btn btn-primary" disabled={saving || conflict || !dirty}>{saving ? t('paymentSettings.saving', 'Saving…') : t('paymentSettings.save', 'Save changes')}</button>
            </div>}
          </footer>
        </form>}
        {!detail && !loadingList && !loadingDetail && (error || listError) && <button type="button" className="btn btn-outline-secondary" onClick={() => setReload((value) => value + 1)}>{t('paymentSettings.reload', 'Reload settings')}</button>}
      </div>
    </div>
  </div>;
}
